import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { StringParam, useQueryParams } from 'use-query-params'
import dayjs from 'dayjs'

import classes from '../SearchPage.module.scss'

import { useInfiniteQuery } from '@percent/workplace-giving/common/hooks'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { Page } from '@percent/workplace-giving/common/components/Layout/Page'
import { SearchPageHeader } from '@percent/workplace-giving/common/components/SearchPageHeader/SearchPageHeader'
import { mapInfiniteData } from '@percent/workplace-giving/utils/mapInfiniteData'
import { useInfiniteScroll } from '@percent/workplace-giving/common/hooks/useInfiniteScroll/useInfinteScroll'
import { Flex, IllustratedMessage, Text } from '@percent/lemonade'
import { getAccountGivingActions } from '@percent/workplace-giving/api/account/givingActions/getAccountGivingActions/getAccountGivingActions'
import { useGivingActionInteractions } from '@percent/workplace-giving/common/components/GivingActionCard/useGivingActionInteractions'
import { GivingActionCardSkeleton } from '@percent/workplace-giving/common/components/GivingActionCard/GivingActionCardSkeleton'

const PAGE_SIZE = 10

export function GivingActionsSearchPage() {
  const { t } = useTranslation()
  const { track } = useAnalytics()

  const [queryParams, setQueryParams] = useQueryParams({
    query: StringParam
  })

  const { query: searchedQuery } = queryParams
  const { renderCard, overlays } = useGivingActionInteractions({ showCampaignName: true })

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isError } = useInfiniteQuery(
    [
      'getAccountGivingActions',
      {
        date: dayjs().format('YYYY-MM-DD'),
        query: searchedQuery || undefined,
        pageSize: PAGE_SIZE,
        direction: 'DESC' as const
      }
    ],
    getAccountGivingActions,
    {
      staleTime: 0
    }
  )

  const totalResults = data && data.pages.length > 0 ? data.pages[0].totalResults : 0
  const { ref } = useInfiniteScroll(fetchNextPage)

  const mappedData = mapInfiniteData(data)

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [searchedQuery])

  const onQueryChange = (q: string) => {
    setQueryParams({ query: q })
  }

  useEffect(() => {
    if (searchedQuery?.trim().length) {
      track({
        event: 'Search Giving Actions',
        properties: {
          origin: 'Giving Actions Search Page'
        }
      })
    }
  }, [searchedQuery, track])

  return (
    <Page testId="giving-actions-search-page" fullWidthContent>
      <SearchPageHeader
        query={searchedQuery || ''}
        onQueryChange={onQueryChange}
        emptyQueryPageTitle={t('workplace_giving.search.exploreGivingActions')}
        breadcrumbsPageName={t('workplace_giving.search.breadcrumbs.givingActions')}
        searchBarPlaceholder={t('workplace_giving.search.placeholder.givingActions')}
        hideCountrySelector
        allowEmptyQuery
        hideAiPrompt
      />
      <Flex justify="flex-end" align="center" wrap="wrap" gap="xs" mb="md">
        {!isFetching && !isError ? (
          <Text size="small" dataTestId="totalResultsCount">
            {t('workplace_giving.pagination.results', { count: totalResults })}
          </Text>
        ) : null}
      </Flex>
      {isLoading ? (
        <div className={classes.ResultsWrapper}>
          <GivingActionCardSkeleton />
          <GivingActionCardSkeleton />
          <GivingActionCardSkeleton />
          <GivingActionCardSkeleton />
          <GivingActionCardSkeleton />
        </div>
      ) : null}
      {!isLoading && !isError ? (
        <div className={classes.ResultsWrapper}>
          {mappedData?.pages?.map(page => page.map(row => <div key={row.id}>{renderCard(row)}</div>))}
          {hasNextPage && (
            <div ref={ref}>
              <GivingActionCardSkeleton />
            </div>
          )}
        </div>
      ) : null}
      <div className={classes.InfosWrapper}>
        {totalResults === 0 && !isFetching && !isError ? (
          <IllustratedMessage
            illustration={searchedQuery ? 'no-results' : 'nothing-to-see'}
            title={
              searchedQuery
                ? t('workplace_giving.search.noResultsGlobalTitle', {
                    searchTerm: searchedQuery,
                    interpolation: { escapeValue: false }
                  })
                : t('workplace_giving.givingActions.noResults.title')
            }
            description={searchedQuery ? undefined : t('workplace_giving.givingActions.noResults.description')}
          />
        ) : null}
        {isError && (
          <IllustratedMessage
            illustration="general-error"
            title={t('workplace_giving.errors.somethingWentWrong')}
            description={t('workplace_giving.errors.tryReloadingThePage')}
          />
        )}
      </div>
      {overlays}
    </Page>
  )
}
